import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Palette, Sparkles } from 'lucide-react';
import ImageGallery from './ImageGallery';
import ImageGenerator from './ImageGenerator';
import { GeneratedImage } from '../../types/imageStudio';

interface ImageStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string, image: GeneratedImage) => void;
  mode?: 'gallery' | 'create' | 'all';
  campaignId?: string;
}

const ImageStudioModal: React.FC<ImageStudioModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  mode = 'all',
  campaignId
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'gallery' | 'create'>(mode === 'create' ? 'create' : 'gallery');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  if (!isOpen) return null;

  const handleSelect = (image: GeneratedImage) => {
    const url = image.edited_url || image.original_url || '';
    onSelect(url, image);
    onClose();
  };

  const handleGenerated = (image: GeneratedImage) => {
    setRefreshTrigger(prev => prev + 1);
    const url = image.edited_url || image.original_url || '';
    onSelect(url, image);
    onClose();
  };

  const showTabs = mode === 'all';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary-600" />
            {t('imageStudio.title', 'Image Studio')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        {showTabs && (
          <div className="flex gap-1 px-6 py-3 bg-gray-50 dark:bg-gray-900/50">
            <button
              onClick={() => setActiveTab('gallery')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'gallery'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Palette className="w-4 h-4" />
              {t('imageStudio.gallery', 'Gallery')}
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'create'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              {t('imageStudio.create', 'Create')}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {(mode === 'gallery' || (mode === 'all' && activeTab === 'gallery')) && (
            <ImageGallery
              onSelect={handleSelect}
              campaignId={campaignId}
              refreshTrigger={refreshTrigger}
              selectionMode
            />
          )}

          {(mode === 'create' || (mode === 'all' && activeTab === 'create')) && (
            <div className="max-w-2xl mx-auto">
              <ImageGenerator
                onImageGenerated={handleGenerated}
                campaignId={campaignId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageStudioModal;
